import React, { useState } from "react";
import { GoalActualDialog } from "./GoalActualDialog";
import { motion, useReducedMotion } from "framer-motion";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Cell, CartesianGrid } from "recharts";
import { Target, Settings2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, monthLabel } from "./primitives";

const STATUS = {
  ahead: { color: "#059669", label: "Ahead of Pace" },
  behind: { color: "#d97706", label: "Behind Pace" },
  critical: { color: "#e11d48", label: "Behind Pace" },
  neutral: { color: "hsl(200 100% 28%)", label: "" },
};

function Ring({ pct, color, center, sub, reduce }) {
  const R = 54, C = 2 * Math.PI * R;
  const p = Math.max(0, Math.min(100, pct || 0));
  const offset = C * (1 - p / 100);
  return (
    <div className="relative w-[140px] h-[140px] shrink-0" data-testid="goal-ring">
      <svg viewBox="0 0 130 130" className="w-full h-full -rotate-90">
        <circle cx="65" cy="65" r={R} fill="none" stroke="#e9eef3" strokeWidth="12" />
        <motion.circle cx="65" cy="65" r={R} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={C} initial={reduce ? false : { strokeDashoffset: C }} animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-2xl font-bold text-slate-900 leading-none">{center}</p>
        <p className="text-[11px] text-slate-400 mt-1">{sub}</p>
      </div>
    </div>
  );
}

function MonthTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{monthLabel(label)}{d.future ? " · upcoming" : ""}</p>
      <p className="text-slate-500">Goal <span className="font-semibold text-slate-800">{d.target}</span></p>
      {!d.future && <p className="text-slate-500">Actual <span className="font-semibold text-slate-800">{d.actual}</span></p>}
    </div>
  );
}

export function GoalHero({ progress, navigate, canEdit, canManage, onManage, index }) {
  const reduce = useReducedMotion();
  const [drill, setDrill] = useState(false);
  const showManage = canManage ?? canEdit;
  const year = progress?.year;
  if (!progress?.has_goal) {
    return (
      <Panel title={`${year || ""} Recruiting Goal`} icon={Target} testId="goal-hero" index={index}>
        <div className="flex flex-col items-center justify-center py-6 text-center" data-testid="goal-empty">
          <div className="w-11 h-11 rounded-full bg-brand/10 text-brand flex items-center justify-center mb-3"><Target className="w-5 h-5" /></div>
          {showManage ? (
            <>
              <p className="text-sm font-medium text-slate-700">No recruiting goal set for {year}.</p>
              <p className="text-xs text-slate-400 mt-1 mb-3">Set targets so managers can track pace against real results.</p>
              <Button size="sm" onClick={onManage} data-testid="goal-set-btn" className="bg-brand hover:bg-brand-strong text-white"><Settings2 className="w-4 h-4 mr-1.5" />Set Recruiting Goal</Button>
            </>
          ) : <p className="text-sm text-slate-500" data-testid="goal-readonly">No recruiting goal has been configured for this year.</p>}
        </div>
      </Panel>
    );
  }
  const prim = progress.primary;
  const st = STATUS[prim.status] || STATUS.neutral;
  const variance = prim.variance;
  const paceLabel = variance > 0 ? `${variance} Ahead of Pace` : variance < 0 ? `${-variance} Behind Pace` : "On Pace";
  const remaining = Math.max(0, prim.annual_target - prim.annual_actual);
  const cur = progress.current_month_index || 0;
  const chart = prim.monthly_target.map((t, i) => ({ m: `${year}-${String(i + 1).padStart(2, "0")}`, target: t, actual: prim.monthly_actual[i], future: i >= cur }));
  const personal = progress.goal_scope === "personal";
  const who = personal ? (progress.personal_user_name || "Personal") : (progress.scope_district_name || "All My Districts");
  return (
    <Panel title={`${year} ${personal ? "Personal " : ""}Recruiting Goal · ${who}`} icon={Target} testId="goal-hero" index={index}
      action={<div className="flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wide rounded bg-brand/10 text-brand px-2 py-0.5" data-testid="goal-scope-label">{progress.scope_label || "GROUP GOAL"}</span>
        {progress.is_aggregate && <span className="text-[11px] text-slate-400" data-testid="goal-aggregate-note">Combined · {progress.contributing_districts} districts</span>}
        {canManage && <button className="text-xs text-brand font-medium flex items-center gap-1" data-testid="goal-manage" onClick={onManage}><Settings2 className="w-3.5 h-3.5" />Manage Goals</button>}
      </div>}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
        <div className="lg:col-span-4 flex items-center gap-4">
          <button onClick={() => setDrill(true)} title="See the records behind this number" data-testid="goal-actual-drill">
            <Ring pct={prim.pct_annual || 0} color={st.color} center={`${prim.annual_actual} / ${prim.annual_target}`} sub={`${prim.pct_annual ?? 0}%`} reduce={reduce} /></button>
          {drill && <GoalActualDialog year={year} metric={prim.key} progress={progress} label={prim.label} onClose={() => setDrill(false)} />}
          <div>
            <p className="text-sm font-semibold text-slate-800">{prim.label}</p>
            <p className="text-xs text-slate-400 mb-2">{prim.pct_annual ?? 0}% of annual goal</p>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: `${st.color}18`, color: st.color }} data-testid="goal-pace">{paceLabel}</span>
          </div>
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 gap-3">
          {[["YTD Target", prim.ytd_target], ["Actual", prim.ytd_actual], ["Remaining", remaining], ["YTD Pace", prim.pct_ytd != null ? `${prim.pct_ytd}%` : "—"]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-slate-100 bg-slate-50/60 p-2.5" data-testid={`goal-stat-${l.replace(/\s/g, "-").toLowerCase()}`}>
              <p className="text-lg font-bold text-slate-900 leading-none">{v}</p>
              <p className="text-[11px] text-slate-500 mt-1">{l}</p>
            </div>
          ))}
        </div>
        <div className="lg:col-span-5">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Monthly Goal vs Actual</p>
          <div className="h-40 w-full" data-testid="goal-monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chart} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                <XAxis dataKey="m" tickFormatter={(m) => monthLabel(m)[0]} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<MonthTip />} cursor={{ fill: "rgba(0,96,144,0.04)" }} />
                <Bar dataKey="actual" radius={[3, 3, 0, 0]} isAnimationActive={!reduce} animationDuration={700}>
                  {chart.map((d, i) => <Cell key={i} fill={d.future ? "#cbd5e1" : "hsl(200 100% 28%)"} />)}
                </Bar>
                <Line dataKey="target" stroke="#d97706" strokeWidth={2} strokeDasharray="4 3" dot={false} isAnimationActive={!reduce} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </Panel>
  );
}
